import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLSpanElement} [viewMorePrevious]
 * @property {HTMLSpanElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** @type {Map<number, string>} */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {string | null} */
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();

    this.#storeImageRatioSettings();
    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );

    // On a normal refresh, start at the top rather than restoring an
    // old scroll position from a long infinite-scroll session.
    if (
      !window.Shopify?.designMode &&
      window.location.pathname.startsWith('/collections/') &&
      performance.getEntriesByType('navigation')[0]?.type === 'reload'
    ) {
      history.scrollRestoration = 'manual';

      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.infinityScrollObserver?.disconnect();

    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;

    this.#imageRatioSetting =
      firstCardGallery.getAttribute('data-image-ratio');
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) {
            await viewTransition.current;
          }

          if (!this.isConnected) return;

          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const currentRefs = this.refs;

            if (entry.target === currentRefs.viewMorePrevious) {
              this.#renderPreviousPage();
            } else if (entry.target === currentRefs.viewMoreNext) {
              this.#renderNextPage();
            }
          }
        },
        { rootMargin: '800px 0px' }
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const lastPage = this.refs.grid?.dataset.lastPage;

    return Boolean(
      lastPage &&
      Number.isInteger(pageInfo.page) &&
      pageInfo.page >= 1 &&
      pageInfo.page <= Number(lastPage)
    );
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePreviousPromise = () => {
      if (type === 'previous') {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePreviousPromise();
      return;
    }

    try {
      if (!this.pages.has(page.page)) {
        await this.#fetchSpecificPage(page.page, page.url);
      }
    } catch (error) {
      console.error('Could not preload products:', error);
    } finally {
      resolvePreviousPromise();
    }
  }

  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('section_id');
      newUrl.searchParams.set('page', String(pageNumber));
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    // The section renderer may add request parameters. Give it its own
    // URL object, never the browser's URL.
    const requestUrl = new URL(pageInfo.url.toString());

    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      requestUrl
    );

    if (this.isConnected) {
      this.pages.set(pageNumber, pageContent);
    }
  }

  async #renderNextPage() {
    const { grid, viewMoreNext } = this.refs;
    if (!grid || this.dataset.loadingNext === 'true') return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    this.dataset.loadingNext = 'true';

    const loader = document.createElement('div');
    loader.className = 'sisterhoods-products-loader';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = `
      <span class="sisterhoods-products-loader__spinner" aria-hidden="true"></span>
      <span>Loading more pieces…</span>
    `;

    const showLoader = window.setTimeout(() => {
      if (viewMoreNext?.isConnected) {
        viewMoreNext.insertAdjacentElement('beforebegin', loader);
      } else if (grid.isConnected) {
        grid.insertAdjacentElement('afterend', loader);
      }
    }, 150);

    try {
      let newCards = this.#getGridForPage(nextPage.page);

      if (!newCards) {
        await this.#fetchSpecificPage(
          nextPage.page,
          nextPage.url
        );
        newCards = this.#getGridForPage(nextPage.page);
      }

      if (!newCards?.length || !grid.isConnected) return;

      const lastDisplayedPage = Number(
        grid.lastElementChild?.dataset.page
      );

      if (lastDisplayedPage >= nextPage.page) return;

      const cards = Array.from(newCards);

      if (
        cards.some(
          (card) => Number(card.dataset.page) !== nextPage.page
        )
      ) {
        this.pages.delete(nextPage.page);
        return;
      }

      grid.append(...cards);
      this.pages.delete(nextPage.page);
      this.#processNewElements();

      // Intentionally do NOT change the address bar.
      // The URL stays /collections/earrings while products load.
      requestIdleCallback(() => {
        if (this.isConnected) this.#fetchPage('next');
      });
    } catch (error) {
      console.error('Could not load more products:', error);
    } finally {
      window.clearTimeout(showLoader);
      loader.remove();
      delete this.dataset.loadingNext;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid || this.dataset.loadingPrevious === 'true') return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    this.dataset.loadingPrevious = 'true';

    try {
      let previousCards =
        this.#getGridForPage(previousPage.page);

      if (!previousCards) {
        const promise = new Promise((resolve) => {
          this.#resolvePreviousPagePromise = resolve;
        });

        this.#fetchPage('previous');
        await promise;

        previousCards =
          this.#getGridForPage(previousPage.page);
      }

      if (!previousCards?.length || !grid.isConnected) return;

      const firstDisplayedPage = Number(
        grid.firstElementChild?.dataset.page
      );

      if (firstDisplayedPage <= previousPage.page) return;

      const cards = Array.from(previousCards);

      if (
        cards.some(
          (card) => Number(card.dataset.page) !== previousPage.page
        )
      ) {
        this.pages.delete(previousPage.page);
        return;
      }

      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldPosition = firstElement
        ? firstElement.getBoundingClientRect().top + window.scrollY
        : 0;

      grid.prepend(...cards);
      this.pages.delete(previousPage.page);
      this.#processNewElements();

      // Intentionally do NOT change the address bar.
      if (firstElement) {
        const newPosition =
          firstElement.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
          top: scrollTop + newPosition - oldPosition,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        if (this.isConnected) this.#fetchPage('previous');
      });
    } catch (error) {
      console.error('Could not load previous products:', error);
    } finally {
      delete this.dataset.loadingPrevious;
    }
  }

  #processNewElements() {
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt'
        ? this.#fixAdaptiveAspectRatios()
        : this.#applyFixedAspectRatio();
    });
  }

  #getUnprocessedGalleries() {
    return this.querySelectorAll(
      '.card-gallery:not([data-aspect-ratio-applied])'
    );
  }

  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;

    gallery.style.setProperty(
      '--gallery-aspect-ratio',
      aspectRatio
    );

    gallery
      .querySelectorAll('.product-media-container')
      .forEach((container) => {
        if (container instanceof HTMLElement) {
          container.style.aspectRatio = aspectRatio;
        }
      });

    this.#markAsProcessed(gallery);
  }

  #fixAdaptiveAspectRatios() {
    const galleries = this.#getUnprocessedGalleries();
    if (!galleries.length) return;

    const productRatioCache = new Map();

    galleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId =
        gallery.getAttribute('data-product-id');

      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(
          gallery,
          productRatioCache.get(productId)
        );
        return;
      }

      const img = gallery.querySelector('img');

      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;

        const ratio = this.#getSafeImageAspectRatio(
          img.naturalWidth,
          img.naturalHeight
        );

        if (productId) {
          productRatioCache.set(productId, ratio);
        }

        this.#applyAspectRatioToGallery(gallery, ratio);
      };

      if (img.complete) {
        loadAndSetRatio();
      } else {
        img.addEventListener('load', loadAndSetRatio, {
          once: true,
        });
      }
    });
  }

  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;

    const ratio = this.#getAspectRatioValue(
      this.#imageRatioSetting
    );

    if (!ratio) return;

    const galleries = this.#getUnprocessedGalleries();
    if (!galleries.length) return;

    requestAnimationFrame(() => {
      galleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, ratio);
      });
    });
  }

  static ASPECT_RATIOS = {
    square: '1',
    portrait: '0.8',
    landscape: '1.778',
  };

  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  #getPage(type) {
    const { grid } = this.refs;
    if (!grid) return;

    // Read the cards actually displayed, including newly loaded cards.
    const targetCard =
      type === 'previous'
        ? grid.firstElementChild
        : grid.lastElementChild;

    if (!(targetCard instanceof HTMLElement)) return;

    const currentPage = Number(targetCard.dataset.page);
    if (!Number.isInteger(currentPage) || currentPage < 1) {
      return;
    }

    const page =
      type === 'previous'
        ? currentPage - 1
        : currentPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('page', String(page));
    url.hash = '';

    return { page, url };
  }

  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(
      pageHTML,
      'text/html'
    );

    const gridElement = parsedPage.querySelector(
      '[ref="grid"]'
    );

    if (!gridElement) return;

    return gridElement.querySelectorAll(
      ':scope > [ref="cards[]"]'
    );
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) {
      throw new Error(
        'The section-id attribute is required'
      );
    }

    return id;
  }

  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#resolvePreviousPagePromise?.();
    this.#resolvePreviousPagePromise = null;

    const currentLastPage =
      this.refs.grid?.dataset.lastPage;

    const observer = new MutationObserver(() => {
      const newLastPage =
        this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();
        if (!this.isConnected) return;

        this.#observeViewMore();
        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => observer.disconnect(), 3000);
    }
  };
}
